import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Progress, Sym } from '../../../components/app2';
import authService from '../../../utils/authService';

/**
 * Columna lateral del panel de empresa (rediseño jul-2026):
 * resumen de la empresa + acciones rápidas.
 *
 * Qué cambió respecto de la versión anterior:
 * - Se retiró la tarjeta "Actividad reciente": sus tres renglones estaban
 *   ESCRITOS A MANO ("Hace 2 horas", "Hace 4 horas"…), no venían de ningún dato.
 *   Mostrar actividad inventada es peor que no mostrarla.
 * - Se retiró la tarjeta "¿Necesitas ayuda?": sus botones (Guías, Soporte) no
 *   tenían ninguna acción asociada.
 * - Se conservan el resumen (todo dato real) y las acciones que sí navegan, y
 *   se suma "Ver recibos" que el diseño incluye. "Mi plan" solo aparece para el
 *   administrador de empresa: es su ruta, un super admin no tiene plan propio.
 */
const QuickActionsPanel = ({ companyData, periodos = [] }) => {
  const navigate = useNavigate();
  const role = authService?.getCurrentUser()?.role;

  const employees = companyData?.employees || [];
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter((emp) => emp?.is_active !== false).length;
  // Cobertura REAL del último periodo de nómina. Antes se contaba
  // `receipt_status`, un campo que el backend nunca envía: "Recibos pendientes"
  // salía 0 siempre y la completitud 0%, aunque la empresa tuviera miles de
  // recibos (contradiciendo además a la gráfica de al lado).
  const ultimoPeriodo = periodos[0];
  const conRecibo = ultimoPeriodo?.employees || 0;
  const sinRecibo = Math.max(activeEmployees - conRecibo, 0);
  const completionRate = activeEmployees > 0 && ultimoPeriodo
    ? Math.round((Math.min(conRecibo, activeEmployees) / activeEmployees) * 100)
    : 0;

  const companyState = {
    companyId: companyData?.id || companyData?.company_id,
    companyName: companyData?.company_name || companyData?.businessName,
    companyRfc: companyData?.rfc,
  };

  const actions = [
    {
      id: 'sat',
      icon: 'sync',
      label: 'Sincronizar con el SAT',
      primary: true,
      onClick: () => navigate('/app/payroll-processing', { state: companyState }),
    },
    {
      id: 'recibos',
      icon: 'receipt_long',
      label: 'Ver recibos',
      // A la pantalla de recibos de la EMPRESA. Antes iba al portal de
      // autoservicio, que sin empleado elegido mostraba a uno cualquiera.
      onClick: () => navigate('/app/receipts'),
    },
    {
      id: 'empresa',
      icon: 'settings',
      label: 'Configurar empresa',
      onClick: () => navigate('/app/company-form', { state: { company: companyData, mode: 'edit' } }),
    },
    ...(role === 'company' ? [{
      id: 'plan',
      icon: 'workspace_premium',
      label: 'Mi plan',
      onClick: () => navigate('/app/account/plan'),
    }] : []),
  ];

  const Fila = ({ label, value, tone }) => (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '9px 0', borderTop: '1px solid var(--line)',
      }}
    >
      <span style={{ font: "500 .86rem 'Hanken Grotesk'", color: 'var(--muted)' }}>{label}</span>
      <span style={{ font: "600 .9rem 'Hanken Grotesk'", color: tone || 'var(--heading)' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card pad={false} style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
          <span
            style={{
              width: 38, height: 38, borderRadius: 11, background: 'var(--brand-soft)',
              border: '1px solid var(--brand-line)', color: 'var(--brand-2)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            <Sym name="domain" size={20} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="a2-truncate" style={{ font: "700 1rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
              {companyData?.company_name || companyData?.businessName || companyData?.name || 'Empresa'}
            </div>
            <div className="a2-mono a2-truncate" style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
              {companyData?.rfc || 'RFC no disponible'}
            </div>
          </div>
        </div>

        <Fila label="Total empleados" value={totalEmployees} />
        <Fila label="Empleados activos" value={activeEmployees} />
        <Fila
          label="Sin recibo del último periodo"
          value={sinRecibo}
          tone={sinRecibo > 0 ? 'var(--danger)' : undefined}
        />
        <div style={{ paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
            <span style={{ font: "500 .86rem 'Hanken Grotesk'", color: 'var(--muted)' }}>Cobertura del último periodo</span>
            <span style={{ font: "600 .9rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{completionRate}%</span>
          </div>
          <Progress value={completionRate} label="Cobertura del último periodo de nómina" />
        </div>
      </Card>

      <Card pad={false} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 9 }}>
        <h3 className="a2-card-title" style={{ fontSize: '1.1rem', marginBottom: 6 }}>Acciones rápidas</h3>
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={a.onClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 11,
              cursor: 'pointer', textAlign: 'left', font: "700 .9rem 'Hanken Grotesk'",
              border: `1px solid ${a.primary ? 'var(--brand-line)' : 'var(--line-2)'}`,
              background: a.primary ? 'var(--brand-soft)' : 'var(--surface-2)',
              color: a.primary ? 'var(--brand-2)' : 'var(--heading)',
            }}
          >
            <Sym name={a.icon} size={20} style={{ color: a.primary ? 'var(--brand-2)' : 'var(--muted)' }} />
            {a.label}
          </button>
        ))}
      </Card>
    </div>
  );
};

export default QuickActionsPanel;
